import { useCallback } from 'react';
import type { InitiateCallPayload } from '@servicetitan/contactcenter-agent-inbox-mfe-loader';
import { ContactType } from '@api/generated/graphql';
import type { Contact } from '@api/generated/graphql';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { resolveTenantId } from '@modules/AuthModule/lib/resolveOrgNodeId';
import ModalsModule from '@modules/ModalsModule';
import { ModalId } from '@modules/ModalsModule/types';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';

/**
 * Bridges an MFE click-to-call intent to CCPro's softphone.
 *
 * The agent-inbox / customer-mfe loaders surface the MFE's INITIATE_CALL_EVENT
 * as an `onInitiateCall` prop; this hook is the host-side handler both loader
 * surfaces pass to it — the Calls Customer Panel (CustomerMfeAdapter) and the
 * Texts inbox (InboxTextsPage). The logic was originally local to
 * CustomerMfeAdapter (CCPE-1128); it is extracted here so the Texts surface
 * reuses it rather than duplicating the guards and tenant bridge.
 */
export function useInitiateCall(): (payload: InitiateCallPayload) => void {
  const { user } = useAuth();
  const { phoneConnected } = usePhoneState();

  return useCallback(
    (payload: InitiateCallPayload) => {
      if (
        SoftPhoneModule.isInCall() ||
        !phoneConnected ||
        !user?.outboundCallsAllowed
      ) {
        return;
      }
      // The outbound-call mutation this feeds (CallConfirmation -> useOutboundCall)
      // takes a numeric tenant id, not an OrgNode Guid — that surface is explicitly
      // out of scope for the orgNodeId migration, so bridge back via the user's
      // tenant list rather than threading the Guid further.
      const tenantId = resolveTenantId(user.org.tenants, payload.orgNodeId);
      if (!tenantId) {
        return;
      }
      // The intent payload only carries a raw phone string, not a full Contact.
      // Downstream reads are limited to `type`, `value`, and the optional
      // `memo` (AgentCallerIdConfirmationModal renders it via `?? undefined`,
      // so an absent memo here is a no-op, not a bug) — the required GraphQL
      // fields (`id`, notification flags) are never read, so stubbing them
      // would be fabricated data with no source of truth, not a safer
      // alternative.
      const contact = {
        type: ContactType.Phone,
        value: payload.contact,
      } as Contact;
      ModalsModule.openModal(ModalId.CallConfirmation, {
        contact,
        onClose: ModalsModule.closeActiveModal,
        customer: {
          customerName: payload.customerName,
          tenantId,
          customerId: String(payload.customerId),
        },
      });
    },
    [phoneConnected, user?.outboundCallsAllowed, user?.org.tenants],
  );
}
